import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Optional } from '../../../Common/interfaces';
import { removeIndex } from '../../../Common/Utils';
import { Circle, Label, LabelBodyHeadline, ThoughtBubble } from '../components';
import { thoughtBubbleArrowHeight } from '../components/ThoughtBubble';
import { DeleteIcon } from '../icons';
import { Draggable, Pos } from '../components/Draggable';

export type Relation = {
    text: string;
    x: number;
    y: number;
};

type Props = {
    centerText: string;
    relations: Relation[];
    onChangeRelations?: (relations: Relation[]) => void;
    className?: string;
    relationSize: 'normal' | 'small';
    customRelation?: { element: React.ReactElement; x: number; y: number };
};
export function RelationsCircle({
    className,
    centerText,
    relationSize,
    relations,
    customRelation,
    onChangeRelations,
}: Props) {
    const divRef = useRef<HTMLDivElement>();
    const normalizeDelta: (delta: Pos) => Pos = useCallback((delta) => {
        /*

        The coord-space is percentage from center to edge.

        If the div is 400x400,
        and a Pos is at (53,-26),

        then the actual pixel coord would be (106,-52) from center,
        or (307,142) from top left

        factor: (400x400)/(100x100) -> (4x4)/2 -> (2x2)
        normalize coord: (2x2) * (53, -26) -> (106, -52)

        converting (1, 3) of real into coord-space: real / factor -> (1,3)/(2x2) -> (0.5,1.5)
        */
        const rect = divRef.current.getBoundingClientRect();
        const factor = { x: rect.width / 200, y: rect.height / 200 };
        return {
            x: delta.x / factor.x,
            y: delta.y / factor.y,
        };
    }, []);
    const [selectedIndex, setSelectedIndex] = useState<Optional<number>>(null);
    useEffect(() => {
        function onClick(event: MouseEvent) {
            if (!event.defaultPrevented) {
                setSelectedIndex(null);
            }
        }
        document.addEventListener('click', onClick);
        return () => {
            document.removeEventListener('click', onClick);
        };
    }, []);

    return (
        <div
            className={classNames('relative max-w-narrow', className)}
            ref={divRef}
        >
            <Circle backgroundColor="bg-vibrant-blue-10p">
                <Circle size="w-[72%]" backgroundColor="bg-vibrant-blue-30p">
                    <Circle size="w-[66%]" backgroundColor="bg-vibrant-blue">
                        <LabelBodyHeadline color="text-white">
                            {centerText}
                        </LabelBodyHeadline>
                    </Circle>
                </Circle>
            </Circle>

            {relations.map((x, idx) => {
                const isSelected = idx === selectedIndex;
                const isDraggable = isSelected || selectedIndex == null;
                const isTop = x.y >= 0;
                return (
                    <PositionedElement key={idx} x={x.x} y={x.y}>
                        <Draggable
                            disabled={onChangeRelations == null || !isDraggable}
                            onDragMove={(delta) => {
                                const copy = relations.slice();
                                const normalizedDelta = normalizeDelta(delta);
                                copy[idx] = {
                                    ...x,
                                    x: x.x + normalizedDelta.x,
                                    y: x.y + normalizedDelta.y,
                                };
                                onChangeRelations(copy);
                            }}
                        >
                            <ConfiguredThoughtBubble
                                onClick={
                                    onChangeRelations == null
                                        ? null
                                        : (e) => {
                                              setSelectedIndex(idx);
                                              e.preventDefault();
                                          }
                                }
                                isTop={isTop}
                                isSelected={isSelected}
                                relationSize={relationSize}
                            >
                                <Label
                                    variant={
                                        relationSize === 'normal'
                                            ? 'text-body'
                                            : 'text-body-sm'
                                    }
                                    className="whitespace-nowrap"
                                >
                                    {x.text}
                                </Label>
                            </ConfiguredThoughtBubble>
                        </Draggable>
                        {isSelected && (
                            <button
                                className="absolute rounded-full bg-white p-10 text-falck-red"
                                onClick={(e) => {
                                    setSelectedIndex(null);
                                    onChangeRelations(
                                        removeIndex(relations, idx),
                                    );
                                }}
                                style={{
                                    left: 'calc(100% + 10px)',
                                    top: isTop
                                        ? `${thoughtBubbleArrowHeight}px`
                                        : 0,
                                    filter: 'drop-shadow(0px 0px 5px #00000040)',
                                }}
                            >
                                <DeleteIcon />
                            </button>
                        )}
                    </PositionedElement>
                );
            })}

            {customRelation && (
                <PositionedElement x={customRelation.x} y={customRelation.y}>
                    <ConfiguredThoughtBubble
                        children={customRelation.element}
                        isTop={customRelation.y > 0}
                        relationSize={relationSize}
                    />
                </PositionedElement>
            )}
        </div>
    );
}

type PositionedElementProps = {
    x: number;
    y: number;
    children: React.ReactNode;
};
function PositionedElement({ x, y, ...props }: PositionedElementProps) {
    const isTop = y >= 0;
    const yTransform = isTop ? `` : `translateY(-100%)`;

    return (
        <div
            {...props}
            style={{
                transform: 'translateX(-50%) ' + yTransform,
                top: `${50 + y / 2}%`,
                left: `${50 + x / 2}%`,
            }}
            className="absolute"
        />
    );
}

type ConfiguredThoughtBubbleProps = {
    isSelected?: boolean;
    isTop: boolean;
    relationSize: 'normal' | 'small';
    children: React.ReactNode;
    onClick?: React.MouseEventHandler;
};
function ConfiguredThoughtBubble({
    isSelected,
    isTop,
    relationSize,
    ...props
}: ConfiguredThoughtBubbleProps) {
    return (
        <ThoughtBubble
            {...props}
            backgroundColor="white"
            arrowPosition={isTop ? 'top' : 'bottom'}
            border={{
                width: '2px',
                color: isSelected ? 'border-vibrant-blue' : 'border-white',
            }}
            padding={classNames({
                // These weird numbers are necessary because part of the width comes from the border
                'px-[18px] py-[8px]': relationSize === 'normal',
                'px-[8px] py-[3px]': relationSize === 'small',
            })}
            style={{
                filter: 'drop-shadow(0px 0px 8.48px #00000029)',
            }}
        />
    );
}
